import { useEffect, useRef, useState } from "react";
import { commitPendingFavourites } from "@/components/FavouritesRail";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { fetchStripeCheckoutStatus } from "@/lib/api";

/**
 * Stripe Checkout return page.
 * Polls /api/payments/checkout/status/:session_id until paid (max ~30s),
 * then redirects to the materialised /order/:id page.
 */
const OrderSuccess = () => {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const sessionId = params.get("session_id");
    const [state, setState] = useState({ phase: "checking", attempt: 0, error: null });
    const cancelledRef = useRef(false);

    useEffect(() => {
        if (!sessionId) {
            setState({ phase: "error", attempt: 0, error: "Missing session id" });
            return;
        }
        const maxAttempts = 15; // ~30s
        const poll = async (attempt) => {
            if (cancelledRef.current) return;
            try {
                const r = await fetchStripeCheckoutStatus(sessionId);
                if (r.payment_status === "paid" && r.order_id) {
                    commitPendingFavourites();
                    setState({ phase: "paid", attempt, error: null });
                    setTimeout(() => navigate(`/order/${r.order_id}`, { replace: true }), 600);
                    return;
                }
                if (r.status === "expired") {
                    setState({ phase: "expired", attempt, error: "Payment session expired." });
                    return;
                }
                if (attempt >= maxAttempts) {
                    setState({ phase: "timeout", attempt, error: "Still processing — check your email for confirmation." });
                    return;
                }
                setState({ phase: "checking", attempt: attempt + 1, error: null });
                setTimeout(() => poll(attempt + 1), 2000);
            } catch (e) {
                // Short-circuit on 404 — invalid session id is permanent, no point retrying
                if (e?.response?.status === 404) {
                    setState({ phase: "error", attempt, error: "Payment session not found." });
                    return;
                }
                if (attempt >= maxAttempts) {
                    setState({ phase: "error", attempt, error: "Could not check payment status." });
                    return;
                }
                setTimeout(() => poll(attempt + 1), 2000);
            }
        };
        poll(0);
        return () => { cancelledRef.current = true; };
    }, [sessionId, navigate]);

    return (
        <div className="min-h-screen bg-[#050505] text-[var(--text)] grid place-items-center px-6" data-testid="order-success-page">
            <div className="max-w-md w-full text-center">
                {state.phase === "checking" && (
                    <>
                        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold mb-3">// Payment</div>
                        <h1 className="font-display text-[36px] mb-3">
                            Confirming your <span className="text-gold">payment…</span>
                        </h1>
                        <p className="text-muted mb-8">This usually takes just a couple of seconds.</p>
                        <div className="inline-block animate-pulse font-mono text-[12px] text-gold" data-testid="success-spinner">
                            ⟲ Checking with Stripe…
                        </div>
                    </>
                )}
                {state.phase === "paid" && (
                    <>
                        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold mb-3">// Paid</div>
                        <h1 className="font-display text-[40px] mb-3" data-testid="success-paid">
                            <span className="text-gold">✓</span> Payment received.
                        </h1>
                        <p className="text-muted">Taking you to your order…</p>
                    </>
                )}
                {state.phase === "expired" && (
                    <>
                        <h1 className="font-display text-[32px] mb-3">Session expired</h1>
                        <p className="text-muted mb-6">{state.error}</p>
                        <Link to="/order" className="btn-primary">Back to menu →</Link>
                    </>
                )}
                {(state.phase === "timeout" || state.phase === "error") && (
                    <>
                        <h1 className="font-display text-[32px] mb-3">{state.phase === "timeout" ? "Still processing" : "Something went wrong"}</h1>
                        <p className="text-muted mb-6">{state.error}</p>
                        <Link to="/order" className="btn-primary">Back to menu →</Link>
                    </>
                )}
            </div>
        </div>
    );
};

export default OrderSuccess;
